import React, { isValidElement, useEffect, useState } from 'react';
import { Map, AdvancedMarker, Marker } from '@vis.gl/react-google-maps';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import type { InfoChipConfig, ActionButtonConfig, PinPlacementConfig } from './types';
import { MapInfoChip } from './slots/MapInfoChip';
import { MapActionButton } from './slots/MapActionButton';
import { MapPinPlacementBar } from './slots/MapPinPlacementBar';
import { MapSkeleton } from './MapSkeleton';
import { useMapApiContext } from './MapOverlayProvider';
import { MAPS_AUTH_FAILED_MESSAGE, mapRenderingProps, resolveMapRendering } from './mapsRendering';
import { SectionErrorBoundary } from '../SectionErrorBoundary';
import { markSurfaceMounted } from '../../../lib/datahub/errorLogging/surfaceRegistry';

interface MapOverlayContentProps {
  lat: number | null;
  lng: number | null;
  isLoading: boolean;
  error: string | null;
  zoom: number;
  mapType: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
  markerLabel?: string;
  pinPlacement?: PinPlacementConfig;
  infoStrip?: InfoChipConfig[] | ReactNode;
  sidebar?: ReactNode;
  footer?: ActionButtonConfig[] | ReactNode;
  isMobile: boolean;
}

function renderInfoStrip(infoStrip: InfoChipConfig[] | ReactNode): ReactNode {
  if (isValidElement(infoStrip)) return infoStrip;
  if (Array.isArray(infoStrip)) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 bg-white/90 backdrop-blur-sm border-b border-slate-300">
        {infoStrip.map((chip) => (
          <MapInfoChip key={`${chip.label}-${chip.value}`} {...chip} />
        ))}
      </div>
    );
  }
  return infoStrip as ReactNode;
}

function renderFooter(footer: ActionButtonConfig[] | ReactNode): ReactNode {
  if (isValidElement(footer)) return footer;
  if (Array.isArray(footer)) {
    return (
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-white border-t border-slate-200">
        {footer.map((btn) => (
          <MapActionButton key={btn.label} {...btn} />
        ))}
      </div>
    );
  }
  return footer as ReactNode;
}

export function MapOverlayContent({
  lat,
  lng,
  isLoading,
  error,
  zoom,
  mapType,
  markerLabel,
  pinPlacement,
  infoStrip,
  sidebar,
  footer,
  isMobile,
}: MapOverlayContentProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Pin-placement mode: where the person has put the marker (null = untouched).
  const [placed, setPlaced] = useState<{ lat: number; lng: number } | null>(null);
  // The panel is reused when another map opens over it, so an unsaved point must
  // not survive into a different target or out of placement mode (a viewer would
  // otherwise show someone's abandoned marker instead of the real pin).
  useEffect(() => setPlaced(null), [pinPlacement, lat, lng]);
  const { apiRequested, authFailed } = useMapApiContext();
  // Decided once per mount: WebGL support and the Map ID do not change mid-session.
  const [rendering] = useState(resolveMapRendering);
  const hasCoords = lat != null && lng != null;
  const showMap = !isLoading && hasCoords;
  const markerPosition = placed ?? (hasCoords ? { lat, lng } : undefined);
  const onMarkerDragEnd = pinPlacement
    ? (e: google.maps.MapMouseEvent) => { if (e.latLng) setPlaced({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }
    : undefined;

  // Tell the error reporter a map surface is on screen. Cross-origin Maps JS
  // errors reach `window.onerror` fully redacted ("Script error.", no file, no
  // stack), so this annotation is the only thing that connects one of those to
  // a map being open. Cleanup is the effect's return value, which is what
  // guarantees the mark is paired with the unmount.
  useEffect(() => markSurfaceMounted('map_overlay'), []);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {infoStrip && renderInfoStrip(infoStrip)}

      <div className={`flex flex-1 min-h-0 ${isMobile ? 'flex-col' : 'flex-row'}`}>
        {/* Map area */}
        <div className="flex-1 relative min-h-0">
          {authFailed ? (
            <MapSkeleton error={MAPS_AUTH_FAILED_MESSAGE} />
          ) : showMap && apiRequested ? (
            // A throw from inside the Maps SDK's React components is the ONE
            // Maps failure we can still see with a real stack — it happens
            // during OUR render, on OUR origin, so React's boundary catches it
            // before `window.onerror` ever redacts it. Without this it
            // unmounted the whole route and arrived (if at all) as an
            // unattributable "Script error.".
            <SectionErrorBoundary
              name="Map"
              fallback={<MapSkeleton error="Map could not be displayed" />}
            >
              <Map
                defaultCenter={{ lat, lng }}
                defaultZoom={zoom}
                {...mapRenderingProps(rendering)}
                mapTypeId={mapType}
                gestureHandling="greedy"
                mapTypeControl={true}
                zoomControl={true}
                streetViewControl={false}
                fullscreenControl={false}
                className="w-full h-full"
                onClick={pinPlacement ? (e: { detail: { latLng: google.maps.LatLngLiteral | null } }) => { if (e.detail.latLng) setPlaced(e.detail.latLng); } : undefined}
              >
                {rendering.vector ? (
                  <AdvancedMarker
                    position={markerPosition}
                    draggable={!!pinPlacement}
                    onDragEnd={onMarkerDragEnd}
                  >
                    {markerLabel && (
                      <div className="bg-sky-600 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md whitespace-nowrap">
                        {markerLabel}
                      </div>
                    )}
                  </AdvancedMarker>
                ) : (
                  <Marker
                    position={markerPosition}
                    label={markerLabel}
                    draggable={!!pinPlacement}
                    onDragEnd={onMarkerDragEnd}
                  />
                )}
              </Map>
            </SectionErrorBoundary>
          ) : (
            <MapSkeleton error={error} />
          )}
        </div>

        {/* Sidebar */}
        {sidebar && !isMobile && (
          <div className="relative flex-shrink-0 border-l border-slate-200 bg-white">
            <button
              onClick={() => setSidebarOpen((prev) => !prev)}
              className="absolute -left-3 top-4 z-10 bg-white border border-slate-200 rounded-full p-0.5 shadow-sm hover:bg-slate-50 transition-colors"
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {sidebarOpen ? (
                <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
              ) : (
                <ChevronLeft className="w-3.5 h-3.5 text-slate-500" />
              )}
            </button>
            {sidebarOpen && (
              <div className="w-60 h-full overflow-y-auto p-3">
                {sidebar}
              </div>
            )}
          </div>
        )}

        {/* Mobile sidebar rendered below map */}
        {sidebar && isMobile && (
          <div className="border-t border-slate-200 bg-white overflow-y-auto max-h-48 p-3">
            {sidebar}
          </div>
        )}
      </div>

      {pinPlacement && showMap && <MapPinPlacementBar placed={placed} onSave={pinPlacement.onSave} />}
      {footer && renderFooter(footer)}
    </div>
  );
}
